/**
 * REGISTERING THIS iPAD FOR ENQUIRY NOTIFICATIONS (T1141 M5).
 *
 * The browser half of the loop: register the service worker, subscribe this
 * device to Web Push, and hand the resulting subscription to the server. The
 * service worker itself is `src/client/sw.ts`; the routes are M4's.
 *
 * ⛔ NO `console.` ANYWHERE. `check:copy` bans it in `src/client`, and service-
 * worker registration error handling is exactly where one reflexively writes it.
 * Every failure below is either surfaced to the operator in words (the enable and
 * disable paths, which she initiated) or deliberately silent (the launch path,
 * which she did not) — and the SILENT one has a reader: `readDeviceState()` is
 * what the control on the operator screen renders, so a launch-time failure shows
 * up there as "not active" rather than vanishing.
 *
 * ⛔ NO `localStorage` / `sessionStorage`. Also banned, and also right: a stale
 * "you are subscribed" flag on a device whose subscription the OS has since
 * dropped is worse than no flag, because the screen would assert something false.
 * Everything here is derived live from the browser and the server.
 */
import {
  fetchPushPublicKey,
  postPushSubscription,
  deletePushSubscription,
  pushFailureCopy,
} from './actions.js';

/** ⛔ A FIXED PATH AT THE ORIGIN ROOT, and the location is the mechanism: a
 *  worker served from a subdirectory cannot claim a scope above itself, so
 *  `/sw.js` is what makes `scope: '/'` legal without a
 *  `Service-Worker-Allowed` header. It is emitted there by
 *  `scripts/build-client.mjs` (its own esbuild call, its own outfile). */
const SW_URL = '/sw.js';

/**
 * The id of THIS device's row, as the last successful POST reported it.
 *
 * ⚠️ IN MEMORY ONLY, AND THAT IS FORCED: `localStorage` is banned in this tree.
 * It survives navigation inside the app and dies with the tab, which is exactly
 * the lifetime the disable path needs — and the launch sync re-establishes it on
 * every app start, which is the same path iOS forces on us anyway.
 *
 * ⛔ THE SERVER CANNOT TELL US THIS INSTEAD. `GET /api/push/status` deliberately
 * returns no `endpoint`, because endpoint + keys IS the capability, so a client
 * holding several rows cannot work out which one is the iPad it is running on.
 * The POST's own answer is the only place that mapping exists.
 */
let deviceSubscriptionId: number | null = null;

/** Where this device stands, as far as the browser is concerned. */
export type PushDeviceState =
  /** No service worker or no Push API here at all (a desktop browser without
   *  push, or any non-secure origin). Nothing to offer. */
  | 'unsupported'
  /** The operator (or the device policy) said no. ⛔ Asking again is pointless:
   *  a denied permission cannot be re-prompted from script on any browser. */
  | 'blocked'
  /** Supported, not registered. This is also what a failed launch sync looks
   *  like, which is deliberate — see the module header. */
  | 'off'
  /** Permission granted AND this device holds a live push subscription. */
  | 'on';

export type PushDeviceResult = { ok: true; state: PushDeviceState } | { ok: false; message: string };

/**
 * `navigator.serviceWorker` is typed non-optional by lib.dom and is genuinely
 * ABSENT on an insecure origin and in a private window on some browsers — the
 * same defensive read `Operators.tsx` makes for `navigator.clipboard`, and for
 * the same reason.
 */
function swContainer(): ServiceWorkerContainer | undefined {
  return navigator.serviceWorker as ServiceWorkerContainer | undefined;
}

export function isPushSupported(): boolean {
  return (
    swContainer() !== undefined &&
    typeof PushManager !== 'undefined' &&
    typeof Notification !== 'undefined'
  );
}

/** ⚠️ `Notification.permission` is the ONLY honest source for this. There is no
 *  "did she say yes" flag to keep, and keeping one would drift the moment the
 *  operator changed it in iOS Settings. */
function permission(): NotificationPermission | null {
  return typeof Notification === 'undefined' ? null : Notification.permission;
}

/** Unpadded base64url (what the server's `PUSH_BASE64URL` charset check demands)
 *  from the raw key bytes the browser hands back. */
function bytesToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** The inverse, for the VAPID application server key. ⚠️ `subscribe()` will not
 *  take the base64url STRING on Safari, only bytes.
 *
 *  ⚠️ THE BUFFER IS ALLOCATED EXPLICITLY (`new Uint8Array(new ArrayBuffer(n))`)
 *  RATHER THAN BY LENGTH, and that is a real constraint rather than a flourish:
 *  `new Uint8Array(n)` types as `Uint8Array<ArrayBufferLike>` since TypeScript
 *  5.7, which is NOT assignable to `applicationServerKey`'s `BufferSource`
 *  because `ArrayBufferLike` admits `SharedArrayBuffer`. Naming the ArrayBuffer
 *  narrows it at the source instead of casting it away at the call site. */
function base64UrlToBytes(value: string) {
  const standard = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (standard.length % 4)) % 4);
  const raw = atob(standard + padding);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Register the worker and wait until it is CONTROLLING the page.
 *
 * ⛔ `updateViaCache: 'none'`. Without it the browser may satisfy its own
 * update check for `/sw.js` from the HTTP cache, so a fixed unhashed path can
 * pin a retired worker on a device that never closes.
 *
 * ⛔ AND `await container.ready`, NOT the promise `register()` returns. On the
 * FIRST launch of a freshly installed app the registration exists before it
 * controls the page, and `pushManager.subscribe()` in that window fails
 * SILENTLY — the operator taps enable, nothing happens, and nothing says why.
 */
async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  const container = swContainer();
  if (container === undefined) return null;
  await container.register(SW_URL, { scope: '/', updateViaCache: 'none' });
  return container.ready;
}

/**
 * Is this device already subscribed against the key we are about to use?
 *
 * ⚠️ THE ANSWER DECIDES WHETHER WE DESTROY A WORKING SUBSCRIPTION, so "cannot
 * tell" must read as `false`: an engine that does not expose
 * `PushSubscriptionOptions.applicationServerKey` gets the old unconditional
 * unsubscribe, which is recoverable, rather than a `subscribe()` that could
 * reject with `InvalidStateError` permanently. The defensive read is the same one
 * `swContainer()` makes, and for the same reason - lib.dom types this as always
 * present.
 */
function subscribedWithSameKey(
  existing: PushSubscription,
  wanted: Uint8Array,
): boolean {
  const options = existing.options as PushSubscriptionOptions | undefined;
  const current = options?.applicationServerKey ?? null;
  if (current === null) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === wanted.length && bytes.every((b, i) => b === wanted[i]);
}

/**
 * Replace this device's subscription and tell the server about it.
 *
 * ⛔⛔ `getSubscription()` -> `unsubscribe()` -> `subscribe()` WHENEVER THE
 * APPLICATION SERVER KEY HAS CHANGED, NEVER `subscribe()` ALONE. Calling
 * `subscribe()` with a DIFFERENT `applicationServerKey` while an old subscription
 * is still live rejects with `InvalidStateError` — which would leave every
 * already-registered iPad permanently stuck on the day the production VAPID key
 * is minted, with no recovery available from this screen. Unsubscribing first is
 * what makes a key rotation a non-event, and that arm is unchanged.
 *
 * ⛔ BUT ONLY ON A GENUINE DIFFERENCE (T1141 review, F2). This used to unsubscribe
 * UNCONDITIONALLY, on a path iOS forces us to take on EVERY app launch (there is
 * no `pushsubscriptionchange` event there). The research that motivated the
 * unsubscribe only ever required it for a CHANGED key, so the unconditional form
 * bought nothing and cost one of two things, neither measured: either a new
 * endpoint per launch, churning rows against the 8-row cap and the daily
 * throttle, or - if the push service happens to reissue the same endpoint - a
 * silent dependency on endpoint stability that nothing in this repo tests.
 * ⇒ Compare first. Same key: leave the subscription alone and let `subscribe()`
 * hand back the existing one, which still re-POSTs and so still repairs a row the
 * server has lost. Different key, or unreadable: unsubscribe exactly as before.
 */
async function subscribeAndRegister(registration: ServiceWorkerRegistration): Promise<void> {
  const applicationServerKey = base64UrlToBytes(await fetchPushPublicKey());

  const existing = await registration.pushManager.getSubscription();
  if (existing !== null && !subscribedWithSameKey(existing, applicationServerKey)) {
    await existing.unsubscribe();
  }

  // ⛔ `userVisibleOnly: true`. Chrome REFUSES a subscription without it, and it
  // is also a true statement about this worker: every push it can receive shows
  // a notification (`sw.ts` has exactly one outcome).
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  const p256dh = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  if (p256dh === null || auth === null) {
    // A subscription with no keys cannot be encrypted to, so posting it would
    // store a row that fails at SEND time forever. Refusing here surfaces as
    // "not active", which is the truth.
    await subscription.unsubscribe();
    throw new Error('subscription carried no keys');
  }

  deviceSubscriptionId = await postPushSubscription({
    endpoint: subscription.endpoint,
    keys: { p256dh: bytesToBase64Url(p256dh), auth: bytesToBase64Url(auth) },
  });
}

/**
 * What the control should show. Reads the browser, never a stored flag.
 *
 * ⚠️ 'on' REQUIRES A LIVE SUBSCRIPTION OBJECT, not merely a granted permission.
 * The two come apart in practice: iOS drops subscriptions when an app is
 * offloaded or restored from backup while the permission survives, and a screen
 * keyed on permission alone would report a device as covered when the push
 * service no longer has any route to it.
 */
export async function readDeviceState(): Promise<PushDeviceState> {
  if (!isPushSupported()) return 'unsupported';
  const state = permission();
  if (state === 'denied') return 'blocked';
  const container = swContainer();
  if (container === undefined) return 'unsupported';
  const registration = await container.getRegistration(SW_URL);
  if (registration === undefined) return 'off';
  const subscription = await registration.pushManager.getSubscription();
  return subscription !== null && state === 'granted' ? 'on' : 'off';
}

/**
 * THE APP-LAUNCH PATH. Called from `index.tsx` beside the legacy-capture purge.
 *
 * ⛔ IT NEVER PROMPTS. It returns immediately unless the permission is ALREADY
 * granted, so nothing here can put a permission dialog in front of Helen on a
 * screen she did not ask it from (step 9). The enable path below is the only
 * caller of `requestPermission`.
 *
 * ⛔ AND IT RE-SUBSCRIBES AND RE-POSTS EVERY TIME, WHICH IS NOT BELT-AND-BRACES.
 * iOS supports `pushsubscriptionchange` NOWHERE — there is no event to listen
 * for when the OS rotates or drops a subscription — so an unconditional
 * re-registration at launch is the only mechanism that repairs one. The server
 * upserts on `endpoint` (M4), so the normal case writes no new row, and the
 * per-operator daily throttle is sized for exactly this traffic.
 *
 * ⚠️ FAILURE IS SILENT BY DESIGN, INCLUDING THE 401 ON A PRE-SIGN-IN LAUNCH:
 * `/api/push/public-key` sits below the auth gate, so an app opened at the login
 * screen cannot complete this and must not say anything about it. The repair is
 * `syncOnScreenOpen` below, which the operator screen calls once a session
 * certainly exists.
 */
export async function startPushOnLaunch(): Promise<void> {
  if (!isPushSupported()) return;
  // Register regardless of permission: the worker is what a later `enable` tap
  // needs to be READY, and registering it is unauthenticated and free.
  const registration = await readyRegistration().catch(() => null);
  if (registration === null) return;
  if (permission() !== 'granted') return;
  try {
    await subscribeAndRegister(registration);
  } catch {
    // Deliberately swallowed, deliberately not logged (no `console.` in this
    // tree). The reader is `readDeviceState()` on the operator screen.
  }
}

/** The same repair, run when a screen that certainly has a session opens. Same
 *  silence, same reader. */
export async function syncOnScreenOpen(): Promise<void> {
  await startPushOnLaunch();
}

/**
 * THE GESTURE PATH — the only place a permission is ever requested.
 *
 * ⛔ `Notification.requestPermission()` IS THE FIRST `await` IN THE HANDLER, and
 * the ordering is the whole point: Safari requires a live user activation for
 * the prompt, and an `await` on the key fetch or on `serviceWorker.ready` placed
 * BEFORE it consumes that activation, after which the prompt never appears and
 * the tap looks like it did nothing. `subscribe()` follows immediately, in the
 * same handler, on the permission this call just granted.
 */
export async function enablePushOnThisDevice(): Promise<PushDeviceResult> {
  if (!isPushSupported()) return { ok: true, state: 'unsupported' };

  const granted = await Notification.requestPermission();
  if (granted !== 'granted') {
    return {
      ok: false,
      message:
        'This iPad has not been allowed to show notifications. Turn them on for this app in Settings, then try again.',
    };
  }

  try {
    const registration = await readyRegistration();
    if (registration === null) return { ok: true, state: 'unsupported' };
    await subscribeAndRegister(registration);
    return { ok: true, state: 'on' };
  } catch (e: unknown) {
    return { ok: false, message: pushFailureCopy(e, 'turn notifications on for this iPad') };
  }
}

/**
 * Turn this device off, at BOTH ends.
 *
 * ⛔ THE SERVER ROW GOES FIRST, THEN THE BROWSER SUBSCRIPTION. The other order
 * leaves a row the server will keep pushing to after the operator has been told
 * it is off — and once `unsubscribe()` has run, the endpoint the row holds is
 * the only handle on it and this client no longer has anything to match it by.
 *
 * ⚠️ A 404 FROM THE DELETE IS SUCCESS, and reading it that way is safe rather
 * than sloppy: the route answers 404 identically for "no such row" and for "not
 * yours" so it cannot be used to enumerate ids across operators, and BOTH
 * readings mean the same thing here — there is no row of ours left to remove.
 *
 * ⚠️ WHEN `deviceSubscriptionId` IS NULL the local subscription is still
 * dropped. That state means no POST has succeeded in this app session, so
 * either there is no row, or there is one this client cannot name. Dropping the
 * subscription invalidates its endpoint, and M6 retires a row the push service
 * reports as gone.
 */
export async function disablePushOnThisDevice(): Promise<PushDeviceResult> {
  if (!isPushSupported()) return { ok: true, state: 'unsupported' };
  try {
    if (deviceSubscriptionId !== null) {
      await deletePushSubscription(deviceSubscriptionId).catch((e: unknown) => {
        const status = (e as { status?: unknown }).status;
        if (status !== 404) throw e;
      });
      deviceSubscriptionId = null;
    }
    const container = swContainer();
    const registration = container === undefined ? undefined : await container.getRegistration(SW_URL);
    const subscription = registration === undefined ? null : await registration.pushManager.getSubscription();
    if (subscription !== null) await subscription.unsubscribe();
    return { ok: true, state: 'off' };
  } catch (e: unknown) {
    return { ok: false, message: pushFailureCopy(e, 'turn notifications off for this iPad') };
  }
}
